import { useState, type ReactNode } from "react"
import { useBlocker } from "@tanstack/react-router"

import { Button, Commandbar } from "~/components/design-system/components"
import {
  DialogBodyV2,
  DialogContentV2,
  DialogFooterV2,
  DialogHeaderV2,
  DialogRoot,
  DialogTitle,
} from "~/components/reusable/Dialog"
import type { PermissionRequirement } from "~/hooks/usePermission"
import { pluralize } from "~/utils/formatting"
import { cn } from "~/utils/helpers"

export type SaveCallbacks = { onSaved?: () => void; onFailed?: () => void }

/** Bottom padding for a page's scroll area while the bar shows, so it never covers the last content. */
export const barClearance = (changeCount: number) => (changeCount > 0 ? "pb-20" : undefined)

/** A pending move away from the staged changes, held until the planner saves, discards or stays. */
export type LeaveRequest = { proceed: () => void; reset: () => void }

/** Holds an in-page switch (e.g. to another rule) while changes are staged, for `switchRequest`.
 * Ignored while a save runs, like the sub-tabs. */
export function useGuardedSwitch(hasChanges: boolean, saving: boolean) {
  const [pending, setPending] = useState<(() => void) | null>(null)
  const request: LeaveRequest | null = pending && {
    proceed: () => {
      setPending(null)
      pending()
    },
    reset: () => setPending(null),
  }
  return {
    run: (select: () => void) => {
      if (saving) return
      if (hasChanges) setPending(() => select)
      else select()
    },
    request,
  }
}

type SaveProps = {
  saving: boolean
  saveLabel: string
  requires: PermissionRequirement
  saveDisabledReason: string | undefined
  onSave: (callbacks?: SaveCallbacks) => void
}

function DraftSaveButton({
  saving,
  saveLabel,
  requires,
  saveDisabledReason,
  size,
  onClick,
}: SaveProps & { size?: "xs"; onClick: () => void }) {
  return (
    <Button
      size={size}
      requires={requires}
      loading={saving}
      disabled={saveDisabledReason !== undefined}
      allowedTitle={saveDisabledReason}
      onClick={onClick}
    >
      {saveLabel}
    </Button>
  )
}

/** Bottom Commandbar for unpublished settings edits (Discard all / Show details /
 * Publish N changes). Show details expands the changes above the bar; the count
 * is their number. Leaving the page with unpublished changes asks to publish,
 * discard or stay. */
export function DraftCommandbar({
  changes,
  saving,
  saveLabel,
  requires = "edit",
  saveDisabledReason,
  onDiscard,
  onSave,
  aside,
  switchRequest = null,
}: {
  changes: DraftChange[]
  saving: boolean
  /** Replaces "Publish N changes", e.g. "Create rule". */
  saveLabel?: string
  /** The save mutation's procedure tier. */
  requires?: PermissionRequirement
  /** Disables Save and explains why, e.g. while a staged value is invalid. */
  saveDisabledReason?: string
  onDiscard: () => void
  onSave: (callbacks?: SaveCallbacks) => void
  /** Extra Details column, mounted only while the panel is open so it can compute lazily. */
  aside?: ReactNode
  /** A move within the page (e.g. to another rule) that asks like leaving the page does. */
  switchRequest?: LeaveRequest | null
}) {
  const [open, setOpen] = useState(false)
  const count = changes.length
  const save: SaveProps = {
    saving,
    saveLabel: saveLabel ?? `Publish ${pluralize(count, "change")}`,
    requires,
    saveDisabledReason,
    onSave,
  }
  // Each editing session starts collapsed: the panel closes once the list empties on save or discard.
  if (count === 0 && open) setOpen(false)

  return (
    <>
      <FloatingCommandbar
        details={
          open && (
            <>
              <DraftChangeList changes={changes} />
              {aside}
            </>
          )
        }
      >
        <Commandbar
          inline
          count={count}
          selectedText={count === 1 ? "unpublished change" : "unpublished changes"}
        >
          <Button size="xs" variant="neutral" disabled={saving} onClick={onDiscard}>
            Discard all
          </Button>
          <Button size="xs" variant="neutral" onClick={() => setOpen(!open)}>
            {open ? "Hide details" : "Show details"}
          </Button>
          <DraftSaveButton {...save} size="xs" onClick={() => onSave()} />
        </Commandbar>
      </FloatingCommandbar>
      <DraftLeaveDialog
        count={count}
        save={save}
        onDiscard={onDiscard}
        switchRequest={switchRequest}
      />
    </>
  )
}

/** A bottom bar with an optional details panel above it; `raised` clears the draft bar below. */
export function FloatingCommandbar({
  details,
  raised = false,
  children,
}: {
  details?: ReactNode
  raised?: boolean
  children: ReactNode
}) {
  return (
    <div
      className={cn(
        "pointer-events-none fixed inset-x-0 z-50 flex flex-col items-center gap-2 pb-5",
        raised ? "bottom-14" : "bottom-0",
      )}
    >
      {details && (
        <div className="pointer-events-auto flex max-h-[50vh] w-[min(760px,calc(100vw-32px))] overflow-y-auto rounded-lg border border-strong bg-base shadow-flyout">
          {details}
        </div>
      )}
      <div className="pointer-events-auto">{children}</div>
    </div>
  )
}

function DraftLeaveDialog({
  count,
  save,
  onDiscard,
  switchRequest,
}: {
  count: number
  save: SaveProps
  onDiscard: () => void
  switchRequest: LeaveRequest | null
}) {
  const { saving } = save
  // Another path is another page; search params (a focused store or rule) stay on this one.
  const blocker = useBlocker({
    shouldBlockFn: ({ current, next }) =>
      count > 0 && !saving && next.pathname !== current.pathname,
    enableBeforeUnload: count > 0,
    withResolver: true,
  })
  const request: LeaveRequest | null = blocker.status === "blocked" ? blocker : switchRequest

  return (
    <DialogRoot
      open={request !== null}
      onOpenChange={(open) => !open && !saving && request?.reset()}
    >
      <DialogContentV2 closeButton={false} onInteractOutside={(e) => e.preventDefault()}>
        <DialogHeaderV2>
          <DialogTitle>Publish your changes first?</DialogTitle>
        </DialogHeaderV2>
        <DialogBodyV2>
          <p className="paragraph-base max-w-[50ch] text-fg-subtle">
            {`You have ${pluralize(count, "unpublished change")}. Discarding can't be undone.`}
          </p>
        </DialogBodyV2>
        <DialogFooterV2 className="gap-3">
          <Button variant="transparent" disabled={saving} onClick={() => request?.reset()}>
            Stay
          </Button>
          <div className="flex-1" />
          <Button
            variant="neutral"
            disabled={saving}
            onClick={() => {
              onDiscard()
              request?.proceed()
            }}
          >
            Discard
          </Button>
          <DraftSaveButton
            {...save}
            onClick={() => save.onSave({ onSaved: request?.proceed, onFailed: request?.reset })}
          />
        </DialogFooterV2>
      </DialogContentV2>
    </DialogRoot>
  )
}

export type DraftChange = {
  key: string
  label: string
  /** Stored field name, for rule fields planners discuss by code. */
  code?: string
  before: string
  after: string
}

/** The Details panel's list of staged changes, before → after. */
function DraftChangeList({ changes }: { changes: DraftChange[] }) {
  return (
    <div className="min-w-0 flex-1 px-3.5 py-2.5">
      <div className="label-sm mb-1.5 text-fg-muted">Changes</div>
      <div className="flex max-h-48 flex-col gap-1 overflow-y-auto">
        {changes.map((change) => (
          <div key={change.key} className="flex items-baseline gap-2.5">
            <span className="paragraph-xs min-w-0 flex-1 truncate text-fg-subtle">
              {change.label}
            </span>
            {change.code && (
              <span className="paragraph-xs font-mono text-fg-muted">{change.code}</span>
            )}
            <span className="paragraph-xs tabular-nums text-fg-muted line-through">
              {change.before}
            </span>
            <span className="paragraph-xs text-fg-muted">→</span>
            <span className="paragraph-xs min-w-11 text-right font-semibold tabular-nums">
              {change.after}
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}
